<template>
  <div class="reference-sample">
    <!-- LEFT: TABLE -->
    <a-card class="reference-sample__card" :bordered="true">
      <div class="reference-sample__table">
        <div class="reference-sample__row reference-sample__row_head">
          <div></div>
          <div>Мужчины</div>
          <div>Женщины</div>
        </div>

        <div
            v-for="row in ageRows"
            :key="row.key"
            class="reference-sample__row"
        >
          <div class="reference-sample__label">
            {{ row.label }}
          </div>

          <div class="reference-sample__value">
            {{ row.male }}
          </div>

          <div class="reference-sample__value">
            {{ row.female }}
          </div>
        </div>

        <div class="reference-sample__row reference-sample__row_total">
          <div class="reference-sample__label">
            Всего
          </div>

          <div class="reference-sample__value reference-sample__summary-value">
            {{ sampleData.maleCount }} / {{ sampleData.malePercent }}%
          </div>

          <div class="reference-sample__value reference-sample__summary-value">
            {{ sampleData.femaleCount }} / {{ sampleData.femalePercent }}%
          </div>
        </div>
      </div>
    </a-card>

    <div class="reference-sample__stats">
      <a-card class="reference-sample__stat-card" :bordered="true">
        <div class="reference-sample__stat-row">
          <span>Самый высокий уровень ЭИ</span>
          <span class="value">{{ sampleData.maxGroupScore }}</span>
        </div>

        <div class="reference-sample__stat-row">
          <span>Самый низкий уровень ЭИ</span>
          <span class="value">{{ sampleData.minGroupScore }}</span>
        </div>
      </a-card>

      <a-card class="reference-sample__stat-card" :bordered="true">
        <div class="reference-sample__stat-row">
          <span>Самый высокий уровень KPI</span>
          <span class="value">{{ maxGroupKPI }}</span>
        </div>

        <div class="reference-sample__stat-row">
          <span>Самый низкий уровень KPI</span>
          <span class="value">{{ minGroupKPI }}</span>
        </div>
      </a-card>
    </div>
  </div>
</template>

<script>
import { computed } from "@vue/runtime-core";
import { useStore } from "vuex";

export default {
  name: "ReferenceSampleBlock",

  setup() {
    const store = useStore();

    const referenceReport = computed(
        () => store.state.analytics.referenceReport
    );

    const sexAgeFemale = computed(
        () => referenceReport.value?.sex_and_age_segregation?.female || {}
    );

    const sexAgeMale = computed(
        () => referenceReport.value?.sex_and_age_segregation?.male || {}
    );

    const groupResults = computed(
        () => referenceReport.value?.sex_and_age_segregation?.analytics || {}
    );

    const groupScores = computed(
        () => referenceReport.value?.users || []
    );

    const formatPercent = (value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return "0.0";
      return numericValue.toFixed(1);
    };

    const sampleData = computed(() => {
      const male = sexAgeMale.value;
      const female = sexAgeFemale.value;
      const analytics = groupResults.value;

      const maleCount = Number(male?.count || 0);
      const femaleCount = Number(female?.count || 0);

      return {
        maleCount,
        femaleCount,
        malePercent: formatPercent(male?.percent),
        femalePercent: formatPercent(female?.percent),

        maleYoung: Number(male?.maleYoung || 0),
        maleMiddle: Number(male?.maleMiddle || 0),
        maleAdult: Number(male?.maleAdult || 0),
        maleAged: Number(male?.maleAged || 0),

        femaleYoung: Number(female?.femaleYoung || 0),
        femaleMiddle: Number(female?.femaleMiddle || 0),
        femaleAdult: Number(female?.femaleAdult || 0),
        femaleAged: Number(female?.femaleAged || 0),

        maxGroupScore: analytics?.max_score ?? 0,
        minGroupScore: analytics?.min_score ?? 0,
      };
    });

    const ageRows = computed(() => {
      const d = sampleData.value;

      return [
        { key: "young", label: "От 18 до 25", male: d.maleYoung, female: d.femaleYoung },
        { key: "middle", label: "От 26 до 35", male: d.maleMiddle, female: d.femaleMiddle },
        { key: "adult", label: "От 35 до 55", male: d.maleAdult, female: d.femaleAdult },
        { key: "aged", label: "От 56 и старше", male: d.maleAged, female: d.femaleAged },
      ];
    });

    const minGroupKPI = computed(() => {
      if (!groupScores.value.length) return 0;

      return groupScores.value.reduce((min, user) => {
        const val = Number(user?.kpi || 0);
        return min === null || val < min ? val : min;
      }, null);
    });

    const maxGroupKPI = computed(() => {
      if (!groupScores.value.length) return 0;

      return groupScores.value.reduce((max, user) => {
        const val = Number(user?.kpi || 0);
        return val > max ? val : max;
      }, 0);
    });

    return {
      sampleData,
      ageRows,
      minGroupKPI,
      maxGroupKPI,
    };
  },
};
</script>

<style lang="scss" scoped>
.reference-sample {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
}

.reference-sample__card,
.reference-sample__stat-card {
  border-radius: 8px;

  :deep(.ant-card-body) {
    padding: 16px 20px;
  }
}

.reference-sample__row {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) 130px 130px;
  padding: 8px 0;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  column-gap: 12px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.reference-sample__row_head {
  border-bottom: none;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
  text-align: right;
  padding: 0;
}

.reference-sample__row_total {
  font-weight: 600;
}

.reference-sample__label {
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.reference-sample__value {
  text-align: right;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  &.reference-sample__summary-value {
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.88);
  }
}

.reference-sample__stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reference-sample__stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  span {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
  }

  .value {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.88);
  }
}

@media (max-width: $tablet-breakpoint) {
  .reference-sample {
    grid-template-columns: 1fr;
  }

  .reference-sample__stats {
    flex-direction: row;
  }

  .reference-sample__stat-card {
    flex: 1;
  }
}

@media (max-width: $mobile-breakpoint) {
  .reference-sample__stats {
    flex-direction: column;
  }

  .reference-sample__row {
    grid-template-columns: minmax(120px, 1fr) 100px 100px;
  }
}
</style>